import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2, AlertTriangle, ShieldCheck, Layers, Crosshair } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatDate, appLocale } from '@/lib/dateUtils'
import { escapeHtml } from '@/lib/escape-html'

interface PatternCategory {
    key: string
    label: string
    color: string
    desc: string
}
interface TimelinePoint {
    date: string
    malware: number
    ransomware: number
    config: number
    anomaly: number
    indicator: number
    total: number
    score: number
}
interface HeatObject {
    objectID: string
    objectName: string
}
interface WindowGrid {
    objects: HeatObject[]
    days: string[]
    cells: { x: number; y: number; sev: number }[]
    shown: number
    total: number
}
interface AttackWindow {
    start: string
    end: string
    dayCount: number
    distinctObjects: number
    vaultCount: number
    vaults: string[]
    counts: Record<string, number>
    score: number
    severity: 'high' | 'medium' | 'low'
    topObjects: { objectID: string; objectName: string; events: number }[]
    grid: WindowGrid
}
interface CrossObjectEntry {
    id: number
    date: string | null
    objectID: string
    objectName: string
    vaultID: string
    category: string
    kind: string
    score: number
}
interface CrossObjectData {
    days: number
    categories: PatternCategory[]
    buckets: string[]
    timeline: TimelinePoint[]
    objectHeatmap: { objects: HeatObject[]; buckets: string[]; cells: { x: number; y: number; sev: number }[] }
    windows: AttackWindow[]
    entries: CrossObjectEntry[]
    totals: { threatObjects: number; anomalyObjects: number; indicatorObjects: number; affectedObjects: number }
}

const RANGES = [
    { label: '30d', days: 30 },
    { label: '60d', days: 60 },
    { label: '90d', days: 90 },
    { label: '180d', days: 180 },
    { label: '1y', days: 365 },
]

// Severity scale used by the object × time heatmap (matches the estate RAG language).
const SEV_PIECES = [
    { value: 1, label: 'Indicator', color: '#3b82f6' },
    { value: 2, label: 'Anomaly', color: '#f59e0b' },
    { value: 3, label: 'Config / Threat', color: '#f97316' },
    { value: 4, label: 'Malware / Ransomware', color: '#dc2626' },
]

const fmtShort = (iso: string) => {
    const d = new Date(iso + 'T00:00:00Z')
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
const fmtRange = (a: string, b: string) => (a === b ? fmtShort(a) : `${fmtShort(a)} – ${fmtShort(b)}`)

export function CrossObjectTab() {
    const { t } = useTranslation()
    const [days, setDays] = useState(90)
    const [focus, setFocus] = useState<{ start: number; end: number } | null>(null)
    const [selWin, setSelWin] = useState(0)

    const { data, isLoading } = useQuery({
        queryKey: ['analytics', 'cross-object', days],
        queryFn: async () => {
            const res = await api.get('/api/analytics/cross-object', { params: { days } })
            return (res.data?.data || res.data || {}) as CrossObjectData
        },
    })

    const categories = data?.categories || []
    const timeline = data?.timeline || []
    const buckets = data?.buckets || []
    const windows = data?.windows || []
    const selectedWindow = windows[selWin] || windows[0]
    const grid = selectedWindow?.grid
    const entries = data?.entries || []
    const hasData = timeline.length > 0

    // Estate-pressure timeline: stacked per-category object counts (bars) + total
    // distinct objects affected (line), with detected attack windows shaded.
    const timelineOption = useMemo((): EChartsOption => {
        if (!hasData) return {}
        const catSeries = categories.map((c, i) => ({
            name: c.label,
            type: 'bar' as const,
            stack: 'objects',
            barMaxWidth: 26,
            itemStyle: { color: c.color },
            emphasis: { focus: 'series' as const },
            data: timeline.map((p) => (p as any)[c.key] || 0),
            // Attach the window shading to the first series only.
            ...(i === 0
                ? {
                      markArea: {
                          silent: true,
                          itemStyle: { opacity: 0.12 },
                          data: windows.map((w) => [
                              { xAxis: w.start, itemStyle: { color: w.severity === 'high' ? '#dc2626' : '#f59e0b' } },
                              { xAxis: w.end },
                          ]),
                      },
                  }
                : {}),
        }))

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    if (!params?.length) return ''
                    const idx = params[0].dataIndex
                    const p = timeline[idx]
                    const rows = categories
                        .filter((c) => (p as any)[c.key] > 0)
                        .map((c) => `<span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${escapeHtml(c.color)};margin-right:6px"></span>${escapeHtml(c.label)}: <b>${escapeHtml((p as any)[c.key])}</b>`)
                        .join('<br/>')
                    return `<div style="font-weight:600;margin-bottom:4px">${new Date(p.date + 'T00:00:00Z').toLocaleDateString(appLocale(), { timeZone: 'UTC' })}</div>${rows || '<span style="color:#888">no category detail</span>'}<br/><span style="color:#888">Distinct objects affected: <b>${p.total}</b></span>`
                },
            },
            legend: { top: 0, type: 'scroll', textStyle: { fontSize: 11 } },
            grid: { left: 44, right: 44, top: 34, bottom: 84 },
            xAxis: {
                type: 'category',
                data: buckets,
                axisLabel: { fontSize: 10, color: '#888', rotate: 45, formatter: (v: string) => fmtShort(v) },
                axisTick: { show: false },
            },
            yAxis: [
                { type: 'value', name: 'Objects', nameTextStyle: { fontSize: 10, color: '#888' }, splitLine: { lineStyle: { opacity: 0.4 } }, minInterval: 1 },
                { type: 'value', name: 'Distinct', nameTextStyle: { fontSize: 10, color: '#888' }, splitLine: { show: false }, minInterval: 1 },
            ],
            dataZoom: [
                { type: 'inside', ...(focus ? { startValue: focus.start, endValue: focus.end } : {}) },
                { type: 'slider', height: 16, bottom: 10, ...(focus ? { startValue: focus.start, endValue: focus.end } : {}) },
            ],
            series: [
                ...catSeries,
                {
                    name: 'Distinct objects affected',
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 5,
                    lineStyle: { width: 2, color: '#111827' },
                    itemStyle: { color: '#111827' },
                    data: timeline.map((p) => p.total),
                },
            ],
        }
    }, [hasData, categories, timeline, buckets, windows, focus])

    // Object × time heatmap — scoped to the SELECTED window, and only objects that
    // were actually flagged (sparse). Never the full estate, so it stays readable no
    // matter how many objects exist.
    const heatOption = useMemo((): EChartsOption => {
        if (!grid || !grid.objects.length) return {}
        return {
            tooltip: {
                position: 'top',
                formatter: (p: any) => {
                    const [x, y, sev] = p.data
                    const obj = grid.objects[y]
                    const piece = SEV_PIECES.find((s) => s.value === sev)
                    return `${escapeHtml(fmtShort(grid.days[x]))}<br/><b>${escapeHtml(obj?.objectName || obj?.objectID)}</b><br/>${escapeHtml(piece?.label || 'Flagged')}`
                },
            },
            grid: { left: 130, right: 20, top: 10, bottom: 58 },
            xAxis: {
                type: 'category',
                data: grid.days,
                axisLabel: { fontSize: 10, color: '#888', rotate: 45, formatter: (v: string) => fmtShort(v) },
                axisTick: { show: false },
                axisLine: { show: false },
                splitArea: { show: true },
            },
            yAxis: {
                type: 'category',
                data: grid.objects.map((o) => o.objectName || o.objectID),
                axisLabel: { fontSize: 10, color: '#888', width: 120, overflow: 'truncate' },
                axisTick: { show: false },
                axisLine: { show: false },
                splitArea: { show: true },
            },
            visualMap: {
                type: 'piecewise',
                orient: 'horizontal',
                left: 'center',
                bottom: 0,
                dimension: 2,
                itemWidth: 12,
                itemHeight: 12,
                textStyle: { fontSize: 10 },
                pieces: SEV_PIECES.map((s) => ({ value: s.value, label: s.label, color: s.color })),
            },
            series: [
                {
                    type: 'heatmap',
                    data: grid.cells.map((c) => [c.x, c.y, c.sev]),
                    progressive: 2000,
                    itemStyle: { borderColor: 'rgba(0,0,0,0.04)', borderWidth: 1 },
                    emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.3)' } },
                },
            ],
        }
    }, [grid])

    const focusWindow = (w: AttackWindow, i: number) => {
        setSelWin(i)
        const s = buckets.indexOf(w.start)
        const e = buckets.indexOf(w.end)
        if (s < 0 || e < 0) return
        const pad = Math.max(2, Math.round((e - s) * 0.6))
        setFocus({ start: Math.max(0, s - pad), end: Math.min(buckets.length - 1, e + pad) })
    }

    const heatHeight = grid ? Math.min(760, Math.max(200, grid.objects.length * 17 + 90)) : 0
    const totals = data?.totals

    return (
        <div className="flex flex-col gap-4">
            {/* Header + range selector */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Crosshair className="h-5 w-5" />
                        {t('analytics.crossObject', 'Enterprise Threat Patterns')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">
                        Estate-wide view across all objects — periods where malware, ransomware, analytics threats or
                        behavioural anomalies cluster (together or separately). Broad, simultaneous spikes can signal a
                        coordinated attack.
                    </p>
                </div>
                <div className="flex items-center gap-1 rounded-md border p-0.5">
                    {RANGES.map((r) => (
                        <button
                            key={r.days}
                            onClick={() => { setDays(r.days); setFocus(null); setSelWin(0) }}
                            className={cn(
                                'px-2.5 py-1 text-xs font-medium rounded transition-colors',
                                days === r.days ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                            )}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center h-[320px] text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Analysing estate-wide patterns…
                </div>
            ) : !hasData ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center h-[220px] gap-2 text-sm text-muted-foreground">
                        <ShieldCheck className="h-8 w-8 text-green-500" />
                        No threat, anomaly or indicator activity in the last {days} days.
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Stat strip */}
                    {totals && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard label="Affected objects" value={totals.affectedObjects} tone="slate" />
                            <StatCard label="Threat objects" value={totals.threatObjects} tone="red" />
                            <StatCard label="Anomaly objects" value={totals.anomalyObjects} tone="amber" />
                            <StatCard label="Attack windows" value={windows.length} tone={windows.length ? 'red' : 'green'} />
                        </div>
                    )}

                    {/* Detected coordinated windows — the headline attack signal */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                                <AlertTriangle className={cn('h-4 w-4', windows.length ? 'text-red-500' : 'text-green-500')} />
                                Candidate attack windows
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {windows.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    No coordinated multi-object windows detected — activity is isolated to individual objects
                                    (≥ {3} distinct objects flagged within a short window would raise one here).
                                </p>
                            ) : (
                                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                    {windows.map((w, i) => (
                                        <WindowCard
                                            key={i}
                                            w={w}
                                            categories={categories}
                                            selected={i === selWin}
                                            onFocus={() => focusWindow(w, i)}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Estate-pressure timeline */}
                    <Card>
                        <CardHeader className="pb-1">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Layers className="h-4 w-4" />
                                Estate pressure over time
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">
                                Bars = objects flagged per category each day (an object can appear in more than one). Line = total
                                distinct objects affected. Shaded bands = detected attack windows.
                            </p>
                        </CardHeader>
                        <CardContent>
                            <ReactECharts option={timelineOption} style={{ height: 340 }} opts={{ renderer: 'canvas' }} notMerge />
                        </CardContent>
                    </Card>

                    {/* Flagged-object drill-down — scoped to the SELECTED window, flagged
                        objects only, bounded. Never a full-estate grid. */}
                    {selectedWindow && grid && grid.objects.length > 0 && (
                        <Card>
                            <CardHeader className="pb-1">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Crosshair className="h-4 w-4" />
                                        Flagged objects · {fmtRange(selectedWindow.start, selectedWindow.end)}
                                    </CardTitle>
                                    {windows.length > 1 && (
                                        <div className="flex flex-wrap gap-1">
                                            {windows.map((w, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => focusWindow(w, i)}
                                                    className={cn(
                                                        'px-2 py-0.5 text-xs rounded border transition-colors',
                                                        i === selWin
                                                            ? 'bg-primary text-primary-foreground border-primary'
                                                            : 'text-muted-foreground hover:bg-muted'
                                                    )}
                                                >
                                                    {fmtRange(w.start, w.end)}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Only objects flagged in this window (rows), across its days (columns). Colour = worst signal that day.
                                    {grid.shown < grid.total
                                        ? ` Showing the ${grid.shown} most severe of ${grid.total} flagged objects.`
                                        : ` All ${grid.total} flagged object${grid.total === 1 ? '' : 's'} shown.`}
                                </p>
                            </CardHeader>
                            <CardContent>
                                <ReactECharts option={heatOption} style={{ height: heatHeight }} opts={{ renderer: 'canvas' }} notMerge />
                            </CardContent>
                        </Card>
                    )}

                    {/* Detail table */}
                    {entries.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Notable events</CardTitle>
                            </CardHeader>
                            <CardContent className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-muted-foreground">
                                            <th className="pb-2 pr-4 font-medium">Date</th>
                                            <th className="pb-2 pr-4 font-medium">Object</th>
                                            <th className="pb-2 pr-4 font-medium">Signal</th>
                                            <th className="pb-2 pr-4 font-medium">Vault</th>
                                            <th className="pb-2 font-medium">Score</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((e) => (
                                            <tr key={`${e.category}-${e.id}`} className="border-b last:border-0">
                                                <td className="py-2 pr-4 whitespace-nowrap">{e.date ? formatDate(e.date) : '—'}</td>
                                                <td className="py-2 pr-4">
                                                    <span className="font-medium">{e.objectName || e.objectID}</span>
                                                    <span className="ml-1 font-mono text-xs text-muted-foreground">{e.objectID}</span>
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <span
                                                        className={cn(
                                                            'inline-block rounded px-1.5 py-0.5 text-xs font-medium',
                                                            e.category === 'threat' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                                        )}
                                                    >
                                                        {e.kind}
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-4">{e.vaultID || '—'}</td>
                                                <td className="py-2 font-semibold">{e.score}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    )
}

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'red' | 'amber' | 'green' | 'slate' }) {
    const toneCls = {
        red: 'text-red-600',
        amber: 'text-amber-600',
        green: 'text-green-600',
        slate: 'text-foreground',
    }[tone]
    return (
        <Card>
            <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
                <p className={cn('text-2xl font-bold mt-1', toneCls)}>{value.toLocaleString()}</p>
            </CardContent>
        </Card>
    )
}

function WindowCard({ w, categories, onFocus }: { w: AttackWindow; categories: PatternCategory[]; onFocus: () => void }) {
    const high = w.severity === 'high'
    return (
        <button
            onClick={onFocus}
            className={cn(
                'text-left rounded-lg border p-3 transition-shadow hover:shadow-md',
                high ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-amber-500'
            )}
        >
            <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm">{fmtRange(w.start, w.end)}</span>
                <span
                    className={cn(
                        'text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5',
                        high ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    )}
                >
                    {w.severity}
                </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
                <b className="text-foreground">{w.distinctObjects}</b> objects · {w.vaultCount} vault{w.vaultCount === 1 ? '' : 's'} ·{' '}
                {w.dayCount} day{w.dayCount === 1 ? '' : 's'}
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
                {categories
                    .filter((c) => (w.counts[c.key] || 0) > 0)
                    .map((c) => (
                        <span
                            key={c.key}
                            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
                            style={{ backgroundColor: `${c.color}1a`, color: c.color }}
                        >
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                            {c.label} {w.counts[c.key]}
                        </span>
                    ))}
            </div>
            {w.topObjects.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-2 truncate">
                    {w.topObjects.slice(0, 5).map((o) => o.objectName || o.objectID).join(', ')}
                    {w.topObjects.length > 5 ? ` +${w.topObjects.length - 5}` : ''}
                </p>
            )}
        </button>
    )
}
